// Queries
import DASHBOARD_FETCH_PROGRAM_SUMMARY from "../../graphql/query/DashboardFetchProgramSummary.gql";
import DASHBOARD_FETCH_PROGRESS_SUMMARY from "../../graphql/query/DashboardFetchProgressSummary.gql";
import DASHBOARD_FETCH_SESSIONSBYWEEK from "../../graphql/query/DashboardSessionsByWeek.gql";
import DASHBOARD_FETCH_COACHEESBYPROGRAM from "../../graphql/query/DashboardFetchCoacheesByProgram.gql";
import DASHBOARD_FETCH_FORMRESPONSESBYPROGRAM from "../../graphql/query/DashboardFetchFormResponsesSummary.gql";
import DASHBOARD_FETCH_RENDER_ALIGNMENT_INDEX from "../../graphql/query/FetchRenderAlignmentIndex.gql";
import IS_SUPERVISOR_PROGRAM_ACTIVE from "../../graphql/query/IsSupervisorProgramActive.gql";
import restClient from "../../lib/restClient";

export const namespaced = true;

const initState = () => {
  return {
    programSummary: null,
    sessionsByWeek: null,
    coacheesByProgram: [],
    formResponsesSummary: null,
    formResponsesSummaryRanking: null,
    progressSummary: null,
    alignmentIndex: null,
    isSupervisorActive: false,
    dashboardTrips: null,
    focuses: null,
    progressFocuses: null,
    dashboardProgramsSummary: null,
    tripStatuses: {
      total: 0,
      invited: 0,
      onboarding: 0,
      active: 0,
      paused: 0,
      finished: 0,
      canceled: 0,
      expired: 0,
      scheduled_reactivation: 0,
    },
    programsSummary: null,
    groups: [],
    selectedGroups: [],
    selectedTripStatus: [],
    selectedPace: [],
    dateRange: [],
    filtersChanged: false,
    controllers: {
      progressFocuses: null,
      focuses: null,
      dashboardTrips: null,
      dashboardProgramsSummary: null,
      tripStatuses: null,
      groups: null,
    },
    mainLoading: false,
  };
};

const fetchFormResponses = async (apolloClient, filter) => {
  try {
    const {
      data: { FormResponseSummary },
    } = await apolloClient.query({
      query: DASHBOARD_FETCH_FORMRESPONSESBYPROGRAM,
      fetchPolicy: "no-cache",
      variables: {
        ...filter,
      },
    });
    return { ...FormResponseSummary };
  } catch (error) {
    console.error(error);
    throw error;
  }
};

const isSupervisorProgramActiveQuery = async (apolloClient, programId) => {
  try {
    const {
      data: { IsSupervisorProgramActive },
    } = await apolloClient.query({
      query: IS_SUPERVISOR_PROGRAM_ACTIVE,
      fetchPolicy: "no-cache",
      variables: {
        programId,
      },
    });
    return IsSupervisorProgramActive;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const state = initState();

export const mutations = {
  SET_PROGRAM_SUMMARY(state, programSummary) {
    state.programSummary = programSummary;
  },
  SET_PROGRESS_SUMMARY(state, progressSummary) {
    state.progressSummary = progressSummary;
  },
  SET_SESSIONS_BY_WEEK(state, sessionsByWeek) {
    state.sessionsByWeek = sessionsByWeek;
  },
  SET_COACHEES_BY_PROGRAM(state, coacheesByProgram) {
    state.coacheesByProgram = coacheesByProgram;
  },
  SET_FORM_RESPONSES(state, formResponsesSummary) {
    state.formResponsesSummary = formResponsesSummary;
  },
  SET_ALIGNMENT_INDEX(state, alignmentIndex) {
    state.alignmentIndex = alignmentIndex;
  },
  SET_FORM_RESPONSES_RANKING(state, formResponsesSummary) {
    state.formResponsesSummaryRanking = formResponsesSummary;
  },
  SET_IS_SUPERVISOR_ACTIVE(state, isSupervisorActive) {
    state.isSupervisorActive = isSupervisorActive;
  },
  SET_INIT_STATE(state) {
    Object.assign(state, initState());
  },
  SET_DASHBOARD_TRIPS(state, trips) {
    state.dashboardTrips = trips;
  },
  SET_DASHBOARD_PROGRAMS_SUMMARY(state, programsSummary) {
    state.programsSummary = programsSummary;
  },
  SET_DASHBOARD_TRIP_STATUSES(state, tripStatuses) {
    state.tripStatuses = tripStatuses;
  },
  SET_DASHBOARD_FOCUSES(state, focuses) {
    state.focuses = focuses;
  },
  SET_DASHBOARD_PROGRESS_FOCUSES(state, progressFocuses) {
    state.progressFocuses = progressFocuses;
  },
  SET_GROUPS(state, groups) {
    state.groups = groups;
  },
  SET_SELECTED_GROUPS(state, selectedGroups) {
    state.selectedGroups = selectedGroups;
  },
  SET_SELECTED_TRIP_STATUS(state, selectedTripStatus) {
    state.selectedTripStatus = selectedTripStatus;
  },
  SET_SELECTED_PACE(state, selectedPace) {
    state.selectedPace = selectedPace;
  },
  SET_DATE_RANGE(state, dateRange) {
    state.dateRange = dateRange;
  },
  SET_FILTERS_CHANGED(state, changed) {
    state.filtersChanged = changed;
  },
};

export const actions = {
  async fetchProgramSummary({ commit }, { apolloClient, filter }) {
    try {
      commit("SET_PROGRAM_SUMMARY", null);
      const {
        data: { DashboardFetchProgramSummary },
      } = await apolloClient.query({
        query: DASHBOARD_FETCH_PROGRAM_SUMMARY,
        fetchPolicy: "no-cache",
        variables: {
          data: { filter },
        },
      });
      commit("SET_PROGRAM_SUMMARY", { ...DashboardFetchProgramSummary });
      return { ...DashboardFetchProgramSummary };
    } catch (error) {
      console.error(error);
      throw error;
    }
  },
  async fetchProgressSummary(
    { commit, state },
    { apolloClient, _restClient, variables, groups = [], statuses = [], paces = [] }
  ) {
    try {
      commit("SET_PROGRESS_SUMMARY", null);
      if (_restClient) {
        if (state.controllers.progressSummary) {
          state.controllers.progressSummary.abort();
        }
        state.controllers.progressSummary = new AbortController();
        variables.signal = state.controllers.progressSummary.signal;
        const basePath = process.env?.VUE_APP_BASE_REST_API_PATH || "/api/v1";
        const url = `${basePath}/membership/dashboard/progress-summary`;
        const { data } = await _restClient.post(url, {
          programIdArray: variables,
          groups,
          statuses,
          paces,
        });
        commit("SET_PROGRESS_SUMMARY", data);
        return data;
      }
      const {
        data: { GetProgressSummary },
      } = await apolloClient.query({
        query: DASHBOARD_FETCH_PROGRESS_SUMMARY,
        fetchPolicy: "no-cache",
        variables,
      });
      commit("SET_PROGRESS_SUMMARY", GetProgressSummary);
      return GetProgressSummary;
    } catch (error) {
      if (error.name === "CanceledError" || error.name === "AbortError") {
        console.warn("⚠️ Progress summary request aborted");
        return;
      }
      console.error(error);
      throw error;
    }
  },
  async fetchSessionsByWeek({ commit }, { apolloClient, filter }) {
    try {
      commit("SET_SESSIONS_BY_WEEK", null);
      const {
        data: { DashboardFetchSessionsByWeek },
      } = await apolloClient.query({
        query: DASHBOARD_FETCH_SESSIONSBYWEEK,
        fetchPolicy: "no-cache",
        variables: {
          data: { filter },
        },
      });
      commit("SET_SESSIONS_BY_WEEK", { ...DashboardFetchSessionsByWeek });
      return { ...DashboardFetchSessionsByWeek };
    } catch (error) {
      throw error;
    }
  },
  async fetchCoacheesByProgram({ commit }, { apolloClient, filter }) {
    try {
      commit("SET_COACHEES_BY_PROGRAM", []);
      const {
        data: { DashboardFetchCoacheesByProgram },
      } = await apolloClient.query({
        query: DASHBOARD_FETCH_COACHEESBYPROGRAM,
        fetchPolicy: "no-cache",
        variables: {
          data: { filter },
        },
      });
      commit("SET_COACHEES_BY_PROGRAM", [...DashboardFetchCoacheesByProgram]);
      return [...DashboardFetchCoacheesByProgram];
    } catch (error) {
      console.error(error);
      throw error;
    }
  },
  async fetchFormResponses({ commit }, { apolloClient, filter }) {
    try {
      const formResponses = await fetchFormResponses(apolloClient, filter);
      commit("SET_FORM_RESPONSES", { ...formResponses });
      return { ...formResponses };
    } catch (error) {
      console.error(error);
      throw error;
    }
  },
  async fetchFormResponsesRanking({ commit }, { apolloClient, filter }) {
    try {
      const formResponses = await fetchFormResponses(apolloClient, {
        programId: filter.programId,
        context: "ranking",
      });
      commit("SET_FORM_RESPONSES_RANKING", formResponses);
      return { ...formResponses };
    } catch (error) {
      console.error(error);
      throw error;
    }
  },
  async fetchAlignmentIndex({ commit }, { apolloClient, variables }) {
    try {
      const {
        data: { RenderAlignmentIndex },
      } = await apolloClient.query({
        query: DASHBOARD_FETCH_RENDER_ALIGNMENT_INDEX,
        fetchPolicy: "no-cache",
        variables,
      });
      commit("SET_ALIGNMENT_INDEX", RenderAlignmentIndex);
      return RenderAlignmentIndex;
    } catch (error) {
      console.error(error);
      throw error;
    }
  },
  async fetchSupervisorProgramActive({ commit }, { apolloClient, programId }) {
    try {
      commit("SET_IS_SUPERVISOR_ACTIVE", false);
      const data = await isSupervisorProgramActiveQuery(apolloClient, programId);
      commit("SET_IS_SUPERVISOR_ACTIVE", data);
      return data;
    } catch (error) {
      console.error(error);
      throw error;
    }
  },
  async fetchDashboardTrips({ commit }, payload) {
    try {
      if (!payload || !payload.programIdArray || payload.programIdArray.length === 0)
        return null;
      if (state.controllers.dashboardTrips) {
        state.controllers.dashboardTrips.abort();
      }
      state.controllers.dashboardTrips = new AbortController();

      const basePath = process.env?.VUE_APP_BASE_REST_API_PATH || "/api/v1";
      const url = `${basePath}/membership/dashboard/trips`;

      // Asegurarse de que todos los filtros estén incluidos en la petición
      const requestPayload = {
        ...payload,
        groups: payload.groups || [],
        statuses: payload.statuses || [],
        paces: payload.paces || [],
      };

      const response = await restClient.post(url, requestPayload, {
        signal: state.controllers.dashboardTrips?.signal,
      });
      commit("SET_DASHBOARD_TRIPS", response.data);
      return response.data;
    } catch (error) {
      if (error.name === "CanceledError" || error.name === "AbortError") {
        console.warn("⚠️ Dashboard trips request aborted");
        return;
      }
      console.error(error);
      throw error;
    }
  },
  async fetchProgramsSummary({ commit }, payload) {
    try {
      if (!payload || !payload.programIdArray || payload.programIdArray.length === 0)
        return null;
      const basePath = process.env?.VUE_APP_BASE_REST_API_PATH || "/api/v1";
      const url = `${basePath}/membership/dashboard/summary`;

      // Incluir los filtros de grupos, estados y ritmos en la petición y validar que sean arrays
      const requestPayload = {
        ...payload,
        programIdArray: Array.isArray(payload.programIdArray)
          ? payload.programIdArray
          : [],
        groups: Array.isArray(payload.groups) ? payload.groups : [],
        statuses: Array.isArray(payload.statuses) ? payload.statuses : [],
        paces: Array.isArray(payload.paces) ? payload.paces : [],
      };

      const response = await restClient.post(url, requestPayload);
      commit("SET_DASHBOARD_PROGRAMS_SUMMARY", response.data);
      return response.data;
    } catch (error) {
      console.error(error);
      throw error;
    }
  },
  async fetchTripStatuses({ commit, state }, payload) {
    try {
      if (!payload || !payload.programIdArray || payload.programIdArray.length === 0)
        return null;

      if (state.controllers.tripStatuses) {
        state.controllers.tripStatuses.abort();
      }
      state.controllers.tripStatuses = new AbortController();
      const basePath = process.env?.VUE_APP_BASE_REST_API_PATH || "/api/v1";
      const url = `${basePath}/membership/dashboard/tripStatuses`;

      // Incluir los filtros de grupos, estados y ritmos en la petición y validar que sean arrays
      const requestPayload = {
        ...payload,
        programIdArray: Array.isArray(payload.programIdArray)
          ? payload.programIdArray
          : [],
        groups: Array.isArray(payload.groups) ? payload.groups : [],
        statuses: Array.isArray(payload.statuses) ? payload.statuses : [],
        paces: Array.isArray(payload.paces) ? payload.paces : [],
      };

      const response = await restClient.post(url, requestPayload, {
        signal: state.controllers.tripStatuses.signal,
      });

      // Asegurarnos que la respuesta tiene el formato esperado
      const responseData = response.data || { tripStatuses: state.tripStatuses };
      if (!responseData.tripStatuses) {
        responseData.tripStatuses = state.tripStatuses;
      }

      commit("SET_DASHBOARD_TRIP_STATUSES", responseData);
      return responseData;
    } catch (error) {
      if (error.name === "CanceledError" || error.name === "AbortError") {
        console.warn("⚠️ Trip statuses request aborted");
        return state.tripStatuses;
      }
      console.error(error);
      throw error;
    }
  },
  async fetchProgressFocuses({ commit }, payload) {
    try {
      if (!payload || !payload.programIdArray || payload.programIdArray.length === 0)
        return null;

      const basePath = process.env?.VUE_APP_BASE_REST_API_PATH || "/api/v1";
      const url = `${basePath}/membership/dashboard/progress-focuses`;

      // Incluir los filtros de grupos, estados y ritmos en la petición y validar que sean arrays
      const requestPayload = {
        // ...payload,
        programIds: Array.isArray(payload.programIdArray)
          ? payload.programIdArray.map((id) => id.toString())
          : [],
        tripsGroups: Array.isArray(payload.groups) ? payload.groups : [],
        tripStatuses: Array.isArray(payload.statuses) ? payload.statuses : [],
        tripPaces: Array.isArray(payload.paces) ? payload.paces : [],
      };

      const response = await restClient.post(url, requestPayload);
      commit("SET_DASHBOARD_PROGRESS_FOCUSES", response.data);
      return response.data;
    } catch (error) {
      console.error(error);
      throw error;
    }
  },

  async fetchFocuses({ commit, state }, payload) {
    try {
      if (state.controllers.focuses) {
        state.controllers.focuses.abort();
      }
      state.controllers.focuses = new AbortController();

      // Validar y asegurar que todos los parámetros necesarios son arrays válidos
      const requestPayload = {
        ...payload,
        programIdArray: Array.isArray(payload.programIdArray)
          ? payload.programIdArray
          : [],
        groups: Array.isArray(payload.groups) ? payload.groups : [],
        statuses: Array.isArray(payload.statuses) ? payload.statuses : [],
        paces: Array.isArray(payload.paces) ? payload.paces : [],
      };

      const basePath = process.env?.VUE_APP_BASE_REST_API_PATH || "/api/v1";
      const url = `${basePath}/membership/dashboard/fetch-focuses`;
      const response = await restClient.post(url, requestPayload, {
        signal: state.controllers.focuses?.signal,
      });
      commit("SET_DASHBOARD_FOCUSES", response.data);

      return response.data;
    } catch (error) {
      if (error.name === "CanceledError" || error.name === "AbortError") {
        console.warn("⚠️ Focuses request aborted");
        return;
      }
      console.error(error);
      throw error;
    }
  },

  async fetchGroups({ commit }, payload) {
    try {
      if (
        !payload ||
        !payload.programIdArray ||
        payload.programIdArray.length === 0
      ) {
        commit("SET_GROUPS", []);
        return { success: true, data: [] };
      }

      try {
        // Intentar obtener los datos del servidor
        const response = await restClient.post(
          `${process.env?.VUE_APP_BASE_REST_API_PATH || "/api/v1"}/trips/groups`,
          { programIdArray: payload.programIdArray }
        );

        // Validamos que la respuesta tenga la estructura esperada
        if (response && response.data) {
          const data = response.data;

          // Comprobamos si tenemos un array en data.data (estructura {success: true, data: [...]})
          if (data.success === true && Array.isArray(data.data)) {
            const formattedGroups = data.data.map((group) => {
              // Si el grupo ya es un objeto con text y value, lo usamos así
              if (
                typeof group === "object" &&
                group !== null &&
                group.text &&
                group.value
              ) {
                return group;
              }
              // Si es un string, lo convertimos al formato esperado
              return {
                text: group,
                value: group,
              };
            });

            commit("SET_GROUPS", formattedGroups);

            // Si no hay grupos seleccionados, seleccionar todos automáticamente
            const selectedGroups = this.getters["dashboard/getSelectedGroups"];
            if (!selectedGroups || selectedGroups.length === 0) {
              commit("SET_SELECTED_GROUPS", allGroupValues);
            }

            return { success: true, data: formattedGroups };
          }
          // Si la respuesta es directamente un array, lo usamos
          else if (Array.isArray(data)) {
            const formattedGroups = data.map((group) => {
              if (
                typeof group === "object" &&
                group !== null &&
                group.text &&
                group.value
              ) {
                return group;
              }
              return {
                text: group,
                value: group,
              };
            });

            commit("SET_GROUPS", formattedGroups);

            // Si no hay grupos seleccionados, seleccionar todos automáticamente
            const selectedGroups = this.getters["dashboard/getSelectedGroups"];
            if (!selectedGroups || selectedGroups.length === 0) {
              const allGroupValues = formattedGroups.map((g) => g.value);
              commit("SET_SELECTED_GROUPS", allGroupValues);
            }

            return { success: true, data: formattedGroups };
          } else {
            commit("SET_GROUPS", []);
            return { success: false, data: [] };
          }
        } else {
          commit("SET_GROUPS", []);
          return { success: false, data: [] };
        }
      } catch (error) {
        // Si hay un error al obtener datos del servidor
        console.error("Error al procesar la respuesta de grupos:", error);
        commit("SET_GROUPS", []);
        return {
          success: false,
          error: error.message || "Error al procesar la respuesta de grupos",
          data: [],
        };
      }
    } catch (error) {
      console.error("Error general al obtener grupos:", error);
      commit("SET_GROUPS", []);
      return {
        success: false,
        error: error.message || "Error general al procesar grupos",
        data: [],
      };
    }
  },

  setSelectedGroups({ commit }, groups) {
    commit("SET_SELECTED_GROUPS", groups);
  },

  setSelectedTripStatus({ commit }, status) {
    commit("SET_SELECTED_TRIP_STATUS", status);
  },

  setSelectedPace({ commit }, pace) {
    commit("SET_SELECTED_PACE", pace);
  },

  setDateRange({ commit }, dateRange) {
    commit("SET_DATE_RANGE", dateRange);
  },

  setFiltersChanged({ commit }, changed) {
    commit("SET_FILTERS_CHANGED", changed);
  },

  resetGroups({ commit }) {
    commit("SET_GROUPS", []);
    commit("SET_SELECTED_GROUPS", []);
  },
};

export const getters = {
  currentWeek: (state) => state.sessionsByWeek?.currentWeek ?? 0,
  weeksLabels: (state) => state.sessionsByWeek?.labels,
  getProgramSummary: (state) => ({ ...state.programSummary }),
  getSessionsByWeek: (state) => ({ ...state.sessionsByWeek }),
  getCoacheesByProgram: (state) => [...state.coacheesByProgram],
  getFormResponsesSummary: (state) => ({ ...state.formResponsesSummary }),
  getFormResponsesSummaryRanking: (state) => ({
    ...state.formResponsesSummaryRanking,
  }),
  progressSummary: (state) => state.progressSummary,
  getAlignmentIndex: (state) => state.alignmentIndex,
  isSupervisorActive: (state) => state.isSupervisorActive,
  getDashboardTrips: (state) => state.dashboardTrips,
  getDashboardFocuses: (state) => state.focuses,
  getDashboardProgressFocuses: (state) => state.progressFocuses,
  getDashboardProgramsSummary: (state) => state.programsSummary,
  getDashboardTripStatuses: (state) => state.tripStatuses,
  getDashboardProgramsSummaryDetails: (state) => state.dashboardProgramsSummary,
  getGroups: (state) => state.groups,
  getSelectedGroups: (state) => state.selectedGroups,
  getSelectedTripStatus: (state) => state.selectedTripStatus,
  getSelectedPace: (state) => state.selectedPace,
  getDateRange: (state) => state.dateRange,
  getFiltersChanged: (state) => state.filtersChanged,
};
